/* eslint-disable no-use-before-define */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Typography from '@material-ui/core/Typography';

import CockpitNavigation from './CockpitNavigation';
import { chargerCockpitOperationnel } from './cockpitOperationnelApi';
import { agregerEvenementsEnIncidents } from './incidentAggregateur';

const INTERVALLE_REFRESH_MS = 60000;
const SEUIL_HISTORIQUE_MOIS = 3;

const useStyles = makeStyles((theme) => ({
  page: { paddingTop: theme.spacing(3), paddingBottom: theme.spacing(4), textAlign: 'left' },
  paper: { padding: theme.spacing(2), marginBottom: theme.spacing(2) },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: theme.spacing(1) },
  sectionTitre: { marginTop: theme.spacing(2), marginBottom: theme.spacing(1) },
  muted: { color: '#666', fontSize: 12 },
  paperGuerre: { padding: theme.spacing(2), marginBottom: theme.spacing(2), borderTop: '4px solid #111827' },
  paperCritique: { padding: theme.spacing(2), marginBottom: theme.spacing(2), borderTop: '4px solid #dc2626', backgroundColor: '#fff7f7' },
  tableScroll: { overflowX: 'auto' },
  kpiGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: theme.spacing(1), marginTop: theme.spacing(1) },
  kpiBox: { border: '1px solid #e5e7eb', borderRadius: 6, padding: theme.spacing(1), backgroundColor: '#fff' },
  kpiValeur: { fontWeight: 700, fontSize: 20, lineHeight: 1.2 },
  kpiLabel: { color: '#6b7280', fontSize: 11, textTransform: 'uppercase' },
  version1414: { backgroundColor: '#111827', color: '#fff', fontWeight: 700 },
  signalAge: { fontFamily: 'monospace', fontSize: 11, color: '#374151' },
  chipVert: { backgroundColor: '#ecfdf5', color: '#065f46', fontSize: 11 },
  chipOrange: { backgroundColor: '#fff7ed', color: '#9a3412', fontSize: 11 },
  chipRouge: { backgroundColor: '#fef2f2', color: '#991b1b', fontSize: 11 },
  chipGris: { backgroundColor: '#f3f4f6', color: '#374151', fontSize: 11 },
  chipBleu: { backgroundColor: '#eff6ff', color: '#1d4ed8', fontSize: 11 },
  rowClickable: { cursor: 'pointer', '&:hover': { backgroundColor: '#f9fafb' } },
  alerteTexte: { fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-word', maxWidth: 480 },
  lacune: { borderLeft: '3px solid #d1d5db', padding: theme.spacing(1), backgroundColor: '#f9fafb', color: '#6b7280', fontSize: 12 },
  erreurBox: { borderLeft: '4px solid #f59e0b', padding: theme.spacing(1.5), backgroundColor: '#fff7ed' },
  resumeBar: { display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap', marginTop: theme.spacing(1) },
  detailsHistorique: { marginTop: theme.spacing(1.5), borderTop: '1px solid #e5e7eb', paddingTop: theme.spacing(1) },
  summaryHistorique: { cursor: 'pointer', color: '#374151', fontSize: 13, fontWeight: 600 },
}));

const CockpitOperationnel = () => {
  const classes = useStyles();
  const history = useHistory();
  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [derniereActualisation, setDerniereActualisation] = useState(null);
  const [prochaineActualisationAt, setProchaineActualisationAt] = useState(null);
  const [maintenant, setMaintenant] = useState(Date.now());
  const intervalRef = useRef(null);
  const tickRef = useRef(null);

  const charger = () => {
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
    }
    chargerCockpitOperationnel()
      .then((payload) => {
        setDonnees(payload);
        setErreur('');
        setDerniereActualisation(new Date());
        setChargement(false);
      })
      .catch((err) => {
        setErreur(err.message);
        setChargement(false);
      })
      .then(() => {
        const prochainRefresh = Date.now() + INTERVALLE_REFRESH_MS;
        setProchaineActualisationAt(prochainRefresh);
        intervalRef.current = setTimeout(charger, INTERVALLE_REFRESH_MS);
      });
  };

  useEffect(() => {
    charger();
    tickRef.current = setInterval(() => setMaintenant(Date.now()), 1000);
    return () => {
      clearTimeout(intervalRef.current);
      clearInterval(tickRef.current);
    };
  }, []);

  const ouvrirInvestigation = (q) => {
    const url = urlInvestigationOperationnelle(q);
    if (url) history.push(url);
  };

  return (
    <Container maxWidth={false} className={classes.page}>
      <CockpitNavigation vueActive="terrain" />
      <Grid container spacing={3}>

        <Grid item xs={12}>
          <Paper className={classes.paper}>
            <div className={classes.header}>
              <div>
                <Typography variant="h4">Cockpit operationnel</Typography>
                <Typography variant="body2" className={classes.muted}>
                  Vue terrain — cameras, incidents, uploads, poller.
                  {derniereActualisation && (
                    <span> Actualisé {formatHeure(derniereActualisation)}. </span>
                  )}
                  Refresh automatique 60s.
                </Typography>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="outlined" size="small" onClick={charger} disabled={chargement}>
                  {chargement ? 'Chargement...' : 'Actualiser'}
                </Button>
                <Button
                  variant="text"
                  size="small"
                  onClick={() => history.push('/cockpit/sante-recente')}
                >
                  Sante recente
                </Button>
              </div>
            </div>

            {erreur && (
              <div className={classes.erreurBox} style={{ marginTop: 12 }}>
                <Typography variant="subtitle2">Erreur backend</Typography>
                <Typography variant="body2">{erreur}</Typography>
                <Typography variant="body2" className={classes.muted}>
                  Endpoint attendu: /monitoring/cockpit/operationnel
                </Typography>
              </div>
            )}

            {donnees && <BarreResume donnees={donnees} classes={classes} />}
          </Paper>
        </Grid>

        {donnees && (
          <React.Fragment>
            <SectionTemporalite
              donnees={donnees}
              prochaineActualisationAt={prochaineActualisationAt}
              maintenant={maintenant}
              classes={classes}
            />
            <SectionStatutCam donnees={donnees} classes={classes} ouvrirInvestigation={ouvrirInvestigation} />
            <SectionProjectionConvergence donnees={donnees} classes={classes} />
            <SectionFauxSuccesCritiques
              items={donnees.fauxSuccesCritiques || donnees.fauxSucces}
              classes={classes}
              ouvrirInvestigation={ouvrirInvestigation}
            />
            <SectionIncidentsAggreges donnees={donnees} classes={classes} ouvrirInvestigation={ouvrirInvestigation} />
            <SectionObjetsCritiques
              objetsCritiques={donnees.objetsCritiques}
              fauxSucces={donnees.fauxSucces}
              classes={classes}
              ouvrirInvestigation={ouvrirInvestigation}
            />
            <SectionPoller poller={donnees.poller} classes={classes} />
            <SectionLacunes avertissements={donnees.avertissements} classes={classes} />
          </React.Fragment>
        )}

      </Grid>
    </Container>
  );
};

const BarreResume = ({ donnees, classes }) => {
  const cameras = donnees.cameras || [];
  const statutCam = donnees.statutCam || {};
  const camerasTerrain = donnees.camerasTerrain || (statutCam.cameras || []);
  const terrainDisponible = statutCam.disponible && camerasTerrain.length > 0;
  const camerasRecentesTerrain = separerRecentsHistoriques(camerasTerrain, timestampCameraTerrain).recents;
  const camerasRecentesMonitoring = separerRecentsHistoriques(cameras, timestampCameraMonitoring).recents;
  const actives = terrainDisponible ? camerasRecentesTerrain.filter((c) => projectionTerrainCamera(c).niveau === 'vert') : camerasRecentesMonitoring.filter((c) => !c.silence_camera);
  const incertaines = terrainDisponible ? camerasRecentesTerrain.filter((c) => projectionTerrainCamera(c).niveau === 'orange') : [];
  const silencieuses = terrainDisponible ? camerasRecentesTerrain.filter((c) => projectionTerrainCamera(c).niveau === 'rouge') : camerasRecentesMonitoring.filter((c) => c.silence_camera);
  const nbIncidents = separerRecentsHistoriques(donnees.incidentsCritiques || [], timestampIncident)
    .recents
    .filter((i) => i.severite_operationnelle === 'CRITIQUE')
    .length;
  const nbObjets = separerRecentsHistoriques(donnees.objetsCritiques || [], timestampObjetOperationnel).recents.length;
  const nbFaux = separerRecentsHistoriques(donnees.fauxSucces || [], timestampObjetOperationnel).recents.length;
  const poller = donnees.poller;
  const pollerOk = poller && poller.statut === 'succes';

  return (
    <div className={classes.resumeBar}>
      <Chip size="small" label={`${actives.length} camera(s) active(s) terrain`} className={classes.chipVert} />
      {incertaines.length > 0 && (
        <Chip size="small" label={`${incertaines.length} incertaine(s) terrain`} className={classes.chipOrange} />
      )}
      {silencieuses.length > 0 && (
        <Chip size="small" label={`${silencieuses.length} inactive(s) probable(s)`} className={classes.chipRouge} />
      )}
      {nbIncidents > 0 && (
        <Chip size="small" label={`${nbIncidents} incident(s) critique(s)`} className={classes.chipRouge} />
      )}
      {nbObjets > 0 && (
        <Chip size="small" label={`${nbObjets} objet(s) bloque(s)`} className={classes.chipOrange} />
      )}
      {nbFaux > 0 && (
        <Chip size="small" label={`${nbFaux} faux succes`} className={classes.chipOrange} />
      )}
      <Chip
        size="small"
        label={`Poller: ${poller ? poller.statut : 'inconnu'}`}
        className={pollerOk ? classes.chipVert : classes.chipOrange}
      />
    </div>
  );
};

const SectionTemporalite = ({ donnees, prochaineActualisationAt, maintenant, classes }) => {
  const poller = donnees.poller || {};
  const statutCam = donnees.statutCam || {};
  const genereAt = donnees.genereAt;
  const retardPoller = ageDepuis(poller.dernier_cycle_at || poller.dernier_succes_at);
  const agePreuves = ageDepuis(poller.dernier_fichier_recu_at);
  const ageStatutCam = ageDepuis(statutCam.timestampDerniereMaj);
  const agePayload = ageDepuis(genereAt);
  const secondesAvantRefresh = prochaineActualisationAt
    ? Math.max(0, Math.ceil((prochaineActualisationAt - maintenant) / 1000))
    : null;

  return (
    <Grid item xs={12}>
      <Paper className={classes.paperGuerre}>
        <Typography variant="h6">Fraicheur des donnees et preuves</Typography>
        <Typography variant="body2" className={classes.muted}>
          Les signaux SyncCam arrivent avec delai. Actif ne veut pas dire convergent; silencieux ne veut pas dire mort.
        </Typography>
        <div className={classes.kpiGrid}>
          <Kpi classes={classes} label="Payload cockpit" valeur={agePayload || 'inconnu'} />
          <Kpi classes={classes} label="Prochaine actualisation UI" valeur={secondesAvantRefresh !== null ? `${secondesAvantRefresh}s` : 'en attente'} alerte={secondesAvantRefresh === 0} />
          <Kpi classes={classes} label="StatutCam terrain" valeur={ageStatutCam || 'indisponible'} alerte={statutCam.disponible && ageDepuisMs(statutCam.timestampDerniereMaj) > 2 * 60 * 1000} />
          <Kpi classes={classes} label="Retard poller" valeur={retardPoller || 'inconnu'} alerte={retardPoller && ageDepuisMs(poller.dernier_cycle_at || poller.dernier_succes_at) > 10 * 60 * 1000} />
          <Kpi classes={classes} label="Derniere preuve fichier" valeur={agePreuves || 'aucune'} alerte={agePreuves && ageDepuisMs(poller.dernier_fichier_recu_at) > 30 * 60 * 1000} />
        </div>
      </Paper>
    </Grid>
  );
};

const SectionStatutCam = ({ donnees, classes, ouvrirInvestigation }) => {
  const statutCam = donnees.statutCam || {};
  const toutesCameras = trierParDateDesc(donnees.camerasTerrain || statutCam.cameras || [], timestampCameraTerrain);
  const { recents: cameras, historiques: camerasHistoriques } = separerRecentsHistoriques(toutesCameras, timestampCameraTerrain);
  const resumeTerrain = cameras.reduce((resume, camera) => {
    const niveau = projectionTerrainCamera(camera).niveau;
    return { ...resume, [niveau]: resume[niveau] + 1 };
  }, { vert: 0, orange: 0, rouge: 0, gris: 0 });

  if (!statutCam.disponible && toutesCameras.length === 0) return null;

  return (
    <Grid item xs={12}>
      <Paper className={classes.paperGuerre}>
        <Typography variant="h6">StatutCam terrain temps reel</Typography>
        <Typography variant="body2" className={classes.muted}>
          Source MySQL legacy, issue directement des pings SyncCam. Cette section prime pour la presence terrain immediate.
        </Typography>
        <div className={classes.resumeBar}>
          <Chip size="small" label={`${resumeTerrain.vert} active(s)`} className={classes.chipVert} />
          <Chip size="small" label={`${resumeTerrain.orange} incertaine(s)`} className={resumeTerrain.orange > 0 ? classes.chipOrange : classes.chipGris} />
          <Chip size="small" label={`${resumeTerrain.rouge} inactive(s) probable(s)`} className={resumeTerrain.rouge > 0 ? classes.chipRouge : classes.chipGris} />
          <Chip size="small" label={`Lu: ${ageDepuis(statutCam.timestampLecture) || 'maintenant'}`} className={classes.chipBleu} />
          <Chip size="small" label={`Dernier ping: ${ageDepuis(statutCam.timestampDerniereMaj) || 'inconnu'}`} className={classes.chipBleu} />
        </div>
        {cameras.length > 0 && (
          <div className={classes.tableScroll} style={{ marginTop: 8 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Camera ID</TableCell>
                  <TableCell>Etat terrain</TableCell>
                  <TableCell>Dernier ping</TableCell>
                  <TableCell>Batterie</TableCell>
                  <TableCell>Memoire libre</TableCell>
                  <TableCell>Version APK</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Arena</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cameras.map((camera) => (
                  <LigneCameraTerrain
                    key={camera.telId || camera.camId}
                    camera={camera}
                    classes={classes}
                    ouvrirInvestigation={ouvrirInvestigation}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <DetailsHistoriques
          classes={classes}
          titre={`Voir anciennes cameras terrain (${camerasHistoriques.length})`}
          visible={camerasHistoriques.length > 0}
        >
          <div className={classes.tableScroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Camera ID</TableCell>
                  <TableCell>Etat terrain</TableCell>
                  <TableCell>Dernier ping</TableCell>
                  <TableCell>Batterie</TableCell>
                  <TableCell>Memoire libre</TableCell>
                  <TableCell>Version APK</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Arena</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {camerasHistoriques.map((camera) => (
                  <LigneCameraTerrain
                    key={camera.telId || camera.camId}
                    camera={camera}
                    classes={classes}
                    ouvrirInvestigation={ouvrirInvestigation}
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        </DetailsHistoriques>
      </Paper>
    </Grid>
  );
};

const SectionProjectionConvergence = ({ donnees, classes }) => {
  const projection = calculerProjectionConvergence(donnees);

  return (
    <Grid item xs={12}>
      <Paper className={classes.paperGuerre}>
        <Typography variant="h6">Pipeline vidéo</Typography>
        <div className={classes.resumeBar}>
          <Chip size="small" label={projection.libelle} className={classeProjection(projection.niveau, classes)} />
          <Chip size="small" label={`${projection.nb202} 202 actifs`} className={projection.nb202 > 0 ? classes.chipOrange : classes.chipVert} />
          <Chip size="small" label={`${projection.nbUploadsBloques} uploads bloques`} className={projection.nbUploadsBloques > 0 ? classes.chipOrange : classes.chipVert} />
          <Chip size="small" label={`${projection.nbFauxSucces} faux succes`} className={projection.nbFauxSucces > 0 ? classes.chipRouge : classes.chipVert} />
          <Chip size="small" label={`${projection.nbIncidents} incidents`} className={projection.nbIncidents > 0 ? classes.chipOrange : classes.chipVert} />
        </div>
        <Typography variant="body2" className={classes.muted} style={{ marginTop: 8 }}>
          Heuristique simple: incidents, 202, uploads sans progression, faux succes et age des preuves. A valider par investigation quand un signal vire orange ou rouge.
        </Typography>
      </Paper>
    </Grid>
  );
};

const SectionVersionsApk = ({ versions, classes, ouvrirInvestigation }) => {
  const items = trierParDateDesc(versions || [], timestampObjetOperationnel);

  return (
    <Grid item xs={12}>
      <Paper className={classes.paperGuerre}>
        <Typography variant="h6">Versions APK</Typography>
        <Typography variant="body2" className={classes.muted}>
          Divergence par version. La 1.4.14 est forcee en tete quand elle est observee.
        </Typography>
        {items.length === 0 ? (
          <Typography variant="body2" className={classes.muted} style={{ marginTop: 8 }}>
            Aucune version APK agregee disponible. La vue graphe runtime ou les payloads version ne sont peut-etre pas encore alimentes.
          </Typography>
        ) : (
          <div className={classes.tableScroll}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Version APK</TableCell>
                  <TableCell>Appareils</TableCell>
                  <TableCell>Incidents</TableCell>
                  <TableCell>202 actifs</TableCell>
                  <TableCell>Finalize suspects</TableCell>
                  <TableCell>RAW absentes</TableCell>
                  <TableCell>Uploads bloques</TableCell>
                  <TableCell>Age moyen convergence</TableCell>
                  <TableCell>Derniere activite</TableCell>
                  <TableCell>Projection</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((v) => {
                  const version = v.version_apk || nettoyerVersion(v.version_id || v.version_libelle);
                  const projection = projectionVersion(v);
                  return (
                    <TableRow
                      key={v.version_id || version}
                      className={classes.rowClickable}
                      onClick={() => ouvrirInvestigation(version)}
                    >
                      <TableCell>
                        <Chip
                          size="small"
                          label={version || 'inconnue'}
                          className={version === '1.4.14' ? classes.version1414 : classes.chipBleu}
                        />
                      </TableCell>
                      <TableCell>{nombre(v.nb_appareils)}</TableCell>
                      <TableCell>{chipCompteur(v.nb_incidents, classes)}</TableCell>
                      <TableCell>{chipCompteur(v.nb_202_actifs, classes)}</TableCell>
                      <TableCell>{chipCompteur(v.nb_finalize_suspects, classes)}</TableCell>
                      <TableCell>{chipCompteur(v.nb_raw_absentes, classes)}</TableCell>
                      <TableCell>{chipCompteur(v.nb_uploads_bloques, classes)}</TableCell>
                      <TableCell className={classes.signalAge}>{formatDuree(v.age_moyen_convergence_secondes)}</TableCell>
                      <TableCell className={classes.signalAge}>{ageDepuis(v.dernier_observe_at) || 'inconnu'}</TableCell>
                      <TableCell><Chip size="small" label={projection.label} className={classeProjection(projection.niveau, classes)} /></TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </Paper>
    </Grid>
  );
};

const SectionDetteRuntime = ({ dette, classes, ouvrirInvestigation }) => {
  const d = dette || {};

  return (
    <Grid item xs={12}>
      <Paper className={classes.paperGuerre}>
        <Typography variant="h6">Dette runtime active</Typography>
        <Typography variant="body2" className={classes.muted}>
          Cette section distingue l'activite de la convergence. Un drain actif peut encore etre bloque.
        </Typography>
        <div className={classes.kpiGrid}>
          <Kpi classes={classes} label="Uploads pending" valeur={nombre(d.uploads_pending)} alerte={d.uploads_pending > 0} onClick={() => ouvrirInvestigation('upload_pending')} />
          <Kpi classes={classes} label="202 stagnants" valeur={nombre(d.etats_202_stagnants)} alerte={d.etats_202_stagnants > 0} onClick={() => ouvrirInvestigation('etat_202')} />
          <Kpi classes={classes} label="Backlog" valeur={nombre(d.backlog)} alerte={d.backlog > 0} />
          <Kpi classes={classes} label="Drains actifs" valeur={nombre(d.drains_actifs)} />
          <Kpi classes={classes} label="Objets bloques" valeur={nombre(d.objets_bloques)} alerte={d.objets_bloques > 0} onClick={() => ouvrirInvestigation('bloque')} />
          <Kpi classes={classes} label="Uploads sans progression" valeur={nombre(d.uploads_sans_progression)} alerte={d.uploads_sans_progression > 0} onClick={() => ouvrirInvestigation('upload_bloque_silencieux')} />
          <Kpi classes={classes} label="Age pending/202 max" valeur={ageDepuis(d.plus_vieille_dette_at) || 'inconnu'} alerte={d.plus_vieille_dette_at && ageDepuisMs(d.plus_vieille_dette_at) > 15 * 60 * 1000} />
          <Kpi classes={classes} label="Derniere observation" valeur={ageDepuis(d.derniere_observation_at) || 'inconnu'} />
        </div>
      </Paper>
    </Grid>
  );
};

const SectionFauxSuccesCritiques = ({ items, classes, ouvrirInvestigation }) => {
  const listeComplete = trierParDateDesc(items || [], timestampObjetOperationnel);
  const { recents: liste, historiques } = separerRecentsHistoriques(listeComplete, timestampObjetOperationnel);
  const categories = [
    { cle: 'finalize_ok_sans_raw', label: 'Finalize OK sans RAW' },
    { cle: 'upload_confirme_sans_publication', label: 'Upload confirme sans publication' },
    { cle: 'recovery_avant_first_bytes', label: 'Recovery avant FIRST_BYTES' },
    { cle: 'publication_non_resolvable', label: 'Publication non resolvable' },
    { cle: 'activite_sans_preuve_utile', label: 'Activite sans preuve utile' },
  ];

  return (
    <Grid item xs={12}>
      <Paper className={classes.paperCritique}>
        <Typography variant="h6">Faux succes critiques</Typography>
        <div className={classes.resumeBar}>
          {categories.map((cat) => (
            <Chip
              key={cat.cle}
              size="small"
              label={`${cat.label}: ${liste.filter((item) => categorieFauxSucces(item) === cat.cle).length}`}
              className={liste.some((item) => categorieFauxSucces(item) === cat.cle) ? classes.chipRouge : classes.chipGris}
              onClick={() => ouvrirInvestigation(cat.cle)}
            />
          ))}
        </div>
        {liste.length > 0 ? (
          <div className={classes.tableScroll} style={{ marginTop: 8 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Objet</TableCell>
                  <TableCell>Categorie</TableCell>
                  <TableCell>Etats</TableCell>
                  <TableCell>Age preuve</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {liste.slice(0, 12).map((item) => (
                  <TableRow
                    key={item.id}
                    className={classes.rowClickable}
                    onClick={() => ouvrirInvestigation(item.id)}
                  >
                    <TableCell><strong>{item.libelle || item.id}</strong></TableCell>
                    <TableCell><Chip size="small" label={libelleFauxSucces(categorieFauxSucces(item))} className={classes.chipRouge} /></TableCell>
                    <TableCell>
                      {(item.etats_critiques || []).slice(0, 4).map((etat) => (
                        <Chip key={etat} size="small" label={etat} className={classes.chipOrange} style={{ marginRight: 2 }} />
                      ))}
                    </TableCell>
                    <TableCell className={classes.signalAge}>{ageDepuis(item.dernier_observe_at) || 'inconnu'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <Typography variant="body2" className={classes.muted} style={{ marginTop: 8 }}>
            Aucun faux succes critique recent.
          </Typography>
        )}
        <DetailsHistoriques
          classes={classes}
          titre={`Voir faux succes historiques > 3 mois (${historiques.length})`}
          visible={historiques.length > 0}
        >
          <div className={classes.tableScroll}>
            <Table size="small">
              <TableBody>
                {historiques.map((item) => (
                  <TableRow
                    key={item.id}
                    className={classes.rowClickable}
                    onClick={() => ouvrirInvestigation(item.id)}
                  >
                    <TableCell><strong>{item.libelle || item.id}</strong></TableCell>
                    <TableCell><Chip size="small" label={libelleFauxSucces(categorieFauxSucces(item))} className={classes.chipRouge} /></TableCell>
                    <TableCell>
                      {(item.etats_critiques || []).slice(0, 4).map((etat) => (
                        <Chip key={etat} size="small" label={etat} className={classes.chipOrange} style={{ marginRight: 2 }} />
                      ))}
                    </TableCell>
                    <TableCell className={classes.signalAge}>{ageDepuis(item.dernier_observe_at) || 'inconnu'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DetailsHistoriques>
      </Paper>
    </Grid>
  );
};

const Kpi = ({ classes, label, valeur, alerte, onClick }) => (
  <div
    className={classes.kpiBox}
    onClick={onClick}
    style={{ cursor: onClick ? 'pointer' : 'default', borderColor: alerte ? '#f97316' : '#e5e7eb' }}
  >
    <div className={classes.kpiLabel}>{label}</div>
    <div className={classes.kpiValeur} style={{ color: alerte ? '#b91c1c' : '#111827' }}>{valeur}</div>
  </div>
);

const DetailsHistoriques = ({ classes, titre, visible, children }) => {
  if (!visible) return null;

  return (
    <details className={classes.detailsHistorique}>
      <summary className={classes.summaryHistorique}>{titre}</summary>
      <div style={{ marginTop: 8 }}>{children}</div>
    </details>
  );
};

const LigneCameraTerrain = ({ camera, classes, ouvrirInvestigation }) => (
  <TableRow
    className={classes.rowClickable}
    onClick={() => ouvrirInvestigation(camera.telId || camera.camId)}
  >
    <TableCell>
      <strong>{idCameraTerrain(camera)}</strong>
      {camera.camId && camera.telId && <div className={classes.muted}>camId {camera.camId}</div>}
    </TableCell>
    <TableCell>{chipEtatTerrain(camera, classes)}</TableCell>
    <TableCell className={classes.signalAge}>
      {formatHeure(camera.dernierMaJ)} ({ageDepuis(camera.dernierMaJ) || 'inconnu'})
    </TableCell>
    <TableCell>{chipBatterie(camera.batterie, classes)}</TableCell>
    <TableCell>{formatMemoireGo(camera.memoire)}</TableCell>
    <TableCell>{camera.version ? <Chip size="small" label={camera.version} className={camera.version === '1.4.14' ? classes.version1414 : classes.chipBleu} /> : <span className={classes.muted}>inconnue</span>}</TableCell>
    <TableCell>{camera.client || <span className={classes.muted}>Inconnu</span>}</TableCell>
    <TableCell>
      {camera.arenaNom || camera.arenaId ? (
        <React.Fragment>
          <span>{camera.arenaNom || `Arena ${camera.arenaId}`}</span>
          {camera.arenaNom && camera.arenaId && <div className={classes.muted}>arenaId {camera.arenaId}</div>}
        </React.Fragment>
      ) : (
        <span className={classes.muted}>Inconnue</span>
      )}
    </TableCell>
  </TableRow>
);

const SectionObjetsCritiques = ({ objetsCritiques, fauxSucces, classes, ouvrirInvestigation }) => {
  const objets = trierParDateDesc(objetsCritiques || [], timestampObjetOperationnel);
  const faux = trierParDateDesc(fauxSucces || [], timestampObjetOperationnel);
  const { recents: objetsRecents, historiques: objetsHistoriques } = separerRecentsHistoriques(objets, timestampObjetOperationnel);
  const { recents: fauxRecents, historiques: fauxHistoriques } = separerRecentsHistoriques(faux, timestampObjetOperationnel);
  const total = objets.length + faux.length;
  const totalRecent = objetsRecents.length + fauxRecents.length;
  const totalHistorique = objetsHistoriques.length + fauxHistoriques.length;
  if (total === 0) return null;

  return (
    <Grid item xs={12}>
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitre}>
          Objets bloques / faux succes recents ({totalRecent})
        </Typography>
        {totalRecent > 0 ? (
          <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Objet</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Etats critiques</TableCell>
              <TableCell>Derniere observation</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {objetsRecents.map((obj) => (
              <TableRow
                key={obj.id}
                className={classes.rowClickable}
                onClick={() => ouvrirInvestigation(obj.id)}
              >
                <TableCell><strong>{obj.libelle || obj.id}</strong></TableCell>
                <TableCell className={classes.muted}>{obj.type_objet}</TableCell>
                <TableCell>
                  {(obj.etats_critiques || []).map((e) => (
                    <Chip key={e} size="small" label={e} className={classes.chipOrange} style={{ marginRight: 2 }} />
                  ))}
                </TableCell>
                <TableCell className={classes.muted}>{formatHeure(obj.dernier_observe_at)}</TableCell>
              </TableRow>
            ))}
            {fauxRecents.map((obj) => (
              <TableRow
                key={obj.id}
                className={classes.rowClickable}
                onClick={() => ouvrirInvestigation(obj.id)}
              >
                <TableCell><strong>{obj.libelle || obj.id}</strong></TableCell>
                <TableCell>
                  <span className={classes.muted}>{obj.type_objet}</span>
                  <Chip size="small" label="faux succes" className={classes.chipRouge} style={{ marginLeft: 4 }} />
                </TableCell>
                <TableCell>
                  {(obj.etats_critiques || []).map((e) => (
                    <Chip key={e} size="small" label={e} className={classes.chipRouge} style={{ marginRight: 2 }} />
                  ))}
                </TableCell>
                <TableCell className={classes.muted}>{formatHeure(obj.dernier_observe_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" className={classes.muted}>Aucun objet bloque recent.</Typography>
        )}
        <DetailsHistoriques
          classes={classes}
          titre={`Voir objets et faux succes historiques > 3 mois (${totalHistorique})`}
          visible={totalHistorique > 0}
        >
          <Table size="small">
            <TableBody>
              {objetsHistoriques.map((obj) => (
                <TableRow
                  key={obj.id}
                  className={classes.rowClickable}
                  onClick={() => ouvrirInvestigation(obj.id)}
                >
                  <TableCell><strong>{obj.libelle || obj.id}</strong></TableCell>
                  <TableCell className={classes.muted}>{obj.type_objet}</TableCell>
                  <TableCell>
                    {(obj.etats_critiques || []).map((e) => (
                      <Chip key={e} size="small" label={e} className={classes.chipOrange} style={{ marginRight: 2 }} />
                    ))}
                  </TableCell>
                  <TableCell className={classes.muted}>{formatHeure(obj.dernier_observe_at)}</TableCell>
                </TableRow>
              ))}
              {fauxHistoriques.map((obj) => (
                <TableRow
                  key={obj.id}
                  className={classes.rowClickable}
                  onClick={() => ouvrirInvestigation(obj.id)}
                >
                  <TableCell><strong>{obj.libelle || obj.id}</strong></TableCell>
                  <TableCell>
                    <span className={classes.muted}>{obj.type_objet}</span>
                    <Chip size="small" label="faux succes" className={classes.chipRouge} style={{ marginLeft: 4 }} />
                  </TableCell>
                  <TableCell>
                    {(obj.etats_critiques || []).map((e) => (
                      <Chip key={e} size="small" label={e} className={classes.chipRouge} style={{ marginRight: 2 }} />
                    ))}
                  </TableCell>
                  <TableCell className={classes.muted}>{formatHeure(obj.dernier_observe_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DetailsHistoriques>
      </Paper>
    </Grid>
  );
};

const SectionPoller = ({ poller, classes }) => {
  if (!poller) return null;

  const ok = poller.statut === 'succes';
  const erreur = poller.statut === 'erreur';
  const metriques = poller.metriques || {};
  const dureeCycle = Number.isFinite(Number(metriques.dureeMs))
    ? `${Math.round(Number(metriques.dureeMs))} ms`
    : '-';
  const nbErreurs = Number(metriques.erreurs || 0);
  const nbRotations = Number(metriques.rotationsProbables || 0);

  return (
    <Grid item xs={12} md={6}>
      <Paper className={classes.paper}>
        <Typography variant="h6" className={classes.sectionTitre}>Poller SyncCam (ETL)</Typography>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell style={{ width: 160 }}>Statut</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  label={poller.statut}
                  className={ok ? classes.chipVert : erreur ? classes.chipRouge : classes.chipOrange}
                />
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Dernier succes</TableCell>
              <TableCell className={classes.muted}>{formatHeure(poller.dernier_succes_at) || '—'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Dernier cycle</TableCell>
              <TableCell className={classes.muted}>{formatHeure(poller.dernier_cycle_at) || '—'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Dernier fichier recu</TableCell>
              <TableCell className={classes.muted}>{formatHeure(poller.dernier_fichier_recu_at) || '—'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Dernier fichier capture</TableCell>
              <TableCell className={classes.muted}>{tronquer(poller.dernier_fichier_remote_path || metriques.dernierFichierCapture || '-', 120)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Age derniere preuve</TableCell>
              <TableCell className={classes.muted}>{ageDepuis(poller.dernier_fichier_recu_at) || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Retard poller</TableCell>
              <TableCell className={classes.muted}>{ageDepuis(poller.dernier_cycle_at || poller.dernier_succes_at) || '-'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell>Cycle</TableCell>
              <TableCell className={classes.muted}>
                {dureeCycle} - {metriques.fichiersCandidats || 0} candidat(s) - {metriques.fichiersImportes || 0} importe(s)
              </TableCell>
            </TableRow>
            {(nbErreurs > 0 || nbRotations > 0) && (
              <TableRow>
                <TableCell>Signaux cycle</TableCell>
                <TableCell>
                  {nbErreurs > 0 && <Chip size="small" label={`${nbErreurs} erreur(s)`} className={classes.chipRouge} style={{ marginRight: 6 }} />}
                  {nbRotations > 0 && <Chip size="small" label={`${nbRotations} rotation probable / perte possible`} className={classes.chipOrange} />}
                </TableCell>
              </TableRow>
            )}
            {poller.backoff_ms > 0 && (
              <TableRow>
                <TableCell>Backoff actif</TableCell>
                <TableCell>
                  <Chip size="small" label={`${Math.round(poller.backoff_ms / 1000)}s`} className={classes.chipOrange} />
                </TableCell>
              </TableRow>
            )}
            {poller.erreur_derniere && (
              <TableRow>
                <TableCell>Derniere erreur</TableCell>
                <TableCell>
                  <span className={classes.alerteTexte}>{tronquer(poller.erreur_derniere, 200)}</span>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <Typography variant="body2" className={classes.muted} style={{ marginTop: 8 }}>
          ETL syncstatsdataservice et metriques serveur: non instrumentes dans cette vue.
        </Typography>
      </Paper>
    </Grid>
  );
};

const SectionLacunes = ({ avertissements, classes }) => {
  const lacunes = [
    'Matchs actifs: non disponible dans le monitoring actuel (source: backend .NET /api/matches).',
    'Metriques serveurs (RAM, disque, CPU): non instrumentees.',
    'ETL syncstatsdataservice: non instrumenté separement du poller SyncCam.',
    'Version APK par camera: disponible via investigation, pas agrege ici.',
  ];

  return (
    <Grid item xs={12}>
      <Paper className={classes.paper}>
        <Typography variant="subtitle2" className={classes.muted} style={{ marginBottom: 4 }}>
          Zones non instrumentees dans cette vue
        </Typography>
        {lacunes.map((l, i) => (
          <div key={i} className={classes.lacune} style={{ marginBottom: 4 }}>
            {l}
          </div>
        ))}
        {avertissements && avertissements.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <Typography variant="body2" className={classes.muted}>
              Tables/vues indisponibles: {avertissements.join(', ')}
            </Typography>
          </div>
        )}
      </Paper>
    </Grid>
  );
};

function chipEtatTerrain(camera, classes) {
  const projection = projectionTerrainCamera(camera);
  const classesParNiveau = {
    vert: classes.chipVert,
    orange: classes.chipOrange,
    rouge: classes.chipRouge,
    gris: classes.chipGris,
  };
  return <Chip size="small" label={projection.label} className={classesParNiveau[projection.niveau]} />;
}

function chipBatterie(valeur, classes) {
  const batterie = normaliserBatteriePourcentage(valeur);
  if (batterie === null) return <span className={classes.muted}>inconnue</span>;
  const label = `${Math.round(batterie)}%`;
  if (batterie < 10) return <Chip size="small" label={label} className={classes.chipRouge} />;
  if (batterie < 30) return <Chip size="small" label={label} className={classes.chipOrange} />;
  return <Chip size="small" label={label} className={classes.chipVert} />;
}

function normaliserBatteriePourcentage(valeur) {
  if (valeur === null || valeur === undefined || valeur === '') return null;
  const nombreBatterie = Number(String(valeur).replace('%', '').replace(',', '.').trim());
  if (!Number.isFinite(nombreBatterie)) return null;
  if (nombreBatterie > 0 && nombreBatterie <= 1) return nombreBatterie * 100;
  return nombreBatterie;
}

function projectionTerrainCamera(camera) {
  const agePing = ageDepuisMs(camera && camera.dernierMaJ);
  if (agePing === null) return { niveau: 'rouge', label: 'ping inconnu' };
  if (agePing < 2 * 60 * 1000) return { niveau: 'vert', label: 'actif' };
  if (agePing <= 5 * 60 * 1000) return { niveau: 'orange', label: 'incertain' };
  return { niveau: 'rouge', label: 'inactif probable' };
}

function chipCompteur(valeur, classes) {
  const n = Number(valeur || 0);
  if (n <= 0) return <span className={classes.muted}>0</span>;
  return <Chip size="small" label={n} className={classes.chipOrange} />;
}

function calculerProjectionConvergence(donnees) {
  const dette = donnees.detteRuntime || {};
  const versions = donnees.versionsApk || [];
  const nb202 = Number(dette.etats_202_stagnants || 0) + somme(versions, 'nb_202_actifs');
  const nbUploadsBloques = Number(dette.uploads_sans_progression || 0) + somme(versions, 'nb_uploads_bloques');
  const nbFauxSucces = separerRecentsHistoriques(
    donnees.fauxSuccesCritiques || donnees.fauxSucces || [],
    timestampObjetOperationnel
  ).recents.length;
  const nbIncidents = separerRecentsHistoriques(donnees.incidentsCritiques || [], timestampIncident).recents.length
    + somme(versions, 'nb_incidents');
  const agePreuve = ageDepuisMs(donnees.poller && donnees.poller.dernier_fichier_recu_at);

  if (nbFauxSucces > 0) {
    return { niveau: 'rouge', libelle: 'faux succes suspect', nb202, nbUploadsBloques, nbFauxSucces, nbIncidents };
  }
  if (nb202 > 0 || nbUploadsBloques > 0) {
    return { niveau: 'orange', libelle: 'dette active', nb202, nbUploadsBloques, nbFauxSucces, nbIncidents };
  }
  if (agePreuve > 30 * 60 * 1000) {
    return { niveau: 'orange', libelle: 'stagnation suspecte', nb202, nbUploadsBloques, nbFauxSucces, nbIncidents };
  }
  if (nbIncidents > 0) {
    return { niveau: 'orange', libelle: 'convergence lente', nb202, nbUploadsBloques, nbFauxSucces, nbIncidents };
  }
  return { niveau: 'vert', libelle: 'convergence probable', nb202, nbUploadsBloques, nbFauxSucces, nbIncidents };
}

function projectionVersion(version) {
  const incidents = Number(version.nb_incidents || 0);
  const etats202 = Number(version.nb_202_actifs || 0);
  const finalize = Number(version.nb_finalize_suspects || 0);
  const raw = Number(version.nb_raw_absentes || 0);
  const uploads = Number(version.nb_uploads_bloques || 0);
  if (finalize > 0 || raw > 0) return { niveau: 'rouge', label: 'faux succes suspect' };
  if (etats202 > 0 || uploads > 0) return { niveau: 'orange', label: 'dette active' };
  if (incidents > 0) return { niveau: 'orange', label: 'convergence lente' };
  return { niveau: 'vert', label: 'convergence probable' };
}

function classeProjection(niveau, classes) {
  if (niveau === 'rouge') return classes.chipRouge;
  if (niveau === 'orange') return classes.chipOrange;
  return classes.chipVert;
}

function categorieFauxSucces(item) {
  if (item.categorie_faux_succes) return item.categorie_faux_succes;
  const etats = item.etats_critiques || [];
  if (contientEtat(etats, ['finalize_vide', 'raw_absent', 'raw_vide', 'first_bytes_absent'])) return 'finalize_ok_sans_raw';
  if (contientEtat(etats, ['http_200_sans_publication', 'upload_confirme', 'publication_absente'])) return 'upload_confirme_sans_publication';
  if (contientEtat(etats, ['recovery_avant_first_bytes'])) return 'recovery_avant_first_bytes';
  if (contientEtat(etats, ['media_protected_introuvable', 'publication_non_visible', 'publication_incomplete'])) return 'publication_non_resolvable';
  return 'activite_sans_preuve_utile';
}

function libelleFauxSucces(categorie) {
  const libelles = {
    finalize_ok_sans_raw: 'Finalize OK sans RAW',
    upload_confirme_sans_publication: 'Upload confirme sans publication',
    recovery_avant_first_bytes: 'Recovery avant FIRST_BYTES',
    publication_non_resolvable: 'Publication non resolvable',
    activite_sans_preuve_utile: 'Activite sans preuve utile',
  };
  return libelles[categorie] || categorie;
}

function contientEtat(etats, candidats) {
  return candidats.some((etat) => etats.includes(etat));
}

function somme(items, champ) {
  return (items || []).reduce((total, item) => total + Number(item[champ] || 0), 0);
}

function nombre(valeur) {
  if (valeur === null || valeur === undefined || valeur === '') return '0';
  return String(valeur);
}

function valeurOuTiret(valeur) {
  if (valeur === null || valeur === undefined || valeur === '') return 'â€”';
  return String(valeur);
}

function nettoyerVersion(valeur) {
  if (!valeur) return '';
  return String(valeur).replace(/^version:/, '').replace(/^APK\s+/i, '');
}

function idCameraTerrain(camera) {
  if (!camera) return 'inconnue';
  return camera.cameraId || camera.camId || camera.telId || 'inconnue';
}

function formatMemoireGo(valeur) {
  if (valeur === null || valeur === undefined || valeur === '') return '-';
  const nombreMemoire = Number(String(valeur).replace(',', '.'));
  if (!Number.isFinite(nombreMemoire)) return String(valeur);

  let go = nombreMemoire;
  if (nombreMemoire > 1024 * 1024) {
    go = nombreMemoire / (1024 * 1024 * 1024);
  } else if (nombreMemoire > 1024) {
    go = nombreMemoire / 1024;
  }

  const arrondi = go >= 10 ? Math.round(go) : Math.round(go * 10) / 10;
  return `${arrondi} Go`;
}

function timestampCameraTerrain(camera) {
  return timestampMs(camera && camera.dernierMaJ);
}

function timestampCameraMonitoring(camera) {
  return timestampMs(camera && (
    camera.dernier_signal_terrain_at
    || camera.dernier_heartbeat_at
    || camera.dernier_fichier_recu_at
    || camera.derniere_preuve_utile_at
    || camera.dernier_vrai_progres_at
  ));
}

function timestampIncident(incident) {
  return timestampMs(incident && (incident.ts_event || incident.recu_at));
}

function timestampObjetOperationnel(item) {
  return timestampMs(item && (item.dernier_observe_at || item.derniere_preuve_utile_at || item.recu_at));
}

function timestampMs(ts) {
  if (!ts) return null;
  const d = ts instanceof Date ? ts : new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function jourIsoDepuisTimestamp(ts) {
  const valeur = timestampMs(ts);
  return valeur ? new Date(valeur).toISOString().slice(0, 10) : '';
}

function trierParDateDesc(items, lireTimestamp) {
  return [...(items || [])].sort((a, b) => {
    const ta = lireTimestamp(a);
    const tb = lireTimestamp(b);
    if (ta === null && tb === null) return 0;
    if (ta === null) return 1;
    if (tb === null) return -1;
    return tb - ta;
  });
}

function separerRecentsHistoriques(items, lireTimestamp) {
  const seuil = dateSeuilHistoriqueMs();
  const recents = [];
  const historiques = [];

  (items || []).forEach((item) => {
    const ts = lireTimestamp(item);
    if (ts === null || ts < seuil) {
      historiques.push(item);
    } else {
      recents.push(item);
    }
  });

  return { recents, historiques };
}

function dateSeuilHistoriqueMs() {
  const seuil = new Date();
  seuil.setMonth(seuil.getMonth() - SEUIL_HISTORIQUE_MOIS);
  return seuil.getTime();
}

function ageDepuis(ts) {
  const ms = ageDepuisMs(ts);
  if (ms === null) return '';
  return formatDuree(Math.floor(ms / 1000));
}

function ageDepuisMs(ts) {
  if (!ts) return null;
  const d = ts instanceof Date ? ts : new Date(ts);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Date.now() - d.getTime());
}

function formatDuree(secondes) {
  if (!secondes && secondes !== 0) return '—';
  if (secondes < 60) return `${secondes}s`;
  if (secondes < 3600) return `${Math.floor(secondes / 60)}m`;
  const h = Math.floor(secondes / 3600);
  const m = Math.floor((secondes % 3600) / 60);
  return m > 0 ? `${h}h${m}m` : `${h}h`;
}

function formatHeure(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '—';
  const aujourd_hui = new Date();
  const memeJour = d.toDateString() === aujourd_hui.toDateString();
  if (memeJour) {
    return d.toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleString('fr-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function tronquer(texte, max) {
  if (!texte) return '';
  return texte.length > max ? `${texte.slice(0, max)}…` : texte;
}

function formatHeureSecondes(ts) {
  if (!ts) return '--:--:--';
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '--:--:--';
  return [
    d.getHours().toString().padStart(2, '0'),
    d.getMinutes().toString().padStart(2, '0'),
    d.getSeconds().toString().padStart(2, '0'),
  ].join(':');
}

function chipSeveriteIncident(severite, classes) {
  if (severite === 'CRITIQUE') return classes.chipRouge;
  if (severite === 'DEGRADE') return classes.chipOrange;
  return classes.chipGris;
}

function urlInvestigationOperationnelle(cible) {
  if (!cible) return '';
  if (typeof cible === 'object') {
    const camera = cible.camera || cible.cameraId || cible.cam_id || cible.tel_id;
    if (!camera) return `/cockpit/investigation?q=${encodeURIComponent(cible.event || cible.evenement_type || '')}`;
    const params = new URLSearchParams();
    params.set('camera', camera);
    if (cible.journee) params.set('journee', cible.journee);
    if (cible.incident) params.set('incident', cible.incident);
    if (cible.episode) params.set('episode', cible.episode);
    if (cible.debut) params.set('debut', cible.debut);
    if (cible.fin) params.set('fin', cible.fin);
    if (cible.event) params.set('event', cible.event);
    return `/cockpit/investigation?${params.toString()}`;
  }
  return `/cockpit/investigation?q=${encodeURIComponent(cible)}`;
}

function contexteIncidentOperationnel(incident, evenement) {
  const ev = evenement || (incident.evenements && incident.evenements[0]) || {};
  const debut = incident && incident.debut ? new Date(incident.debut).toISOString() : '';
  const fin = incident && incident.fin ? new Date(incident.fin).toISOString() : debut;
  return {
    camera: incident.cameraId || ev.cam_id || ev.tel_id,
    journee: debut ? debut.slice(0, 10) : jourIsoDepuisTimestamp(ev.ts_event || ev.recu_at),
    incident: ev.ligne_id || ev.id || '',
    episode: incident.incidentId,
    debut,
    fin,
    event: ev.evenement_type || incident.typePrincipal || '',
  };
}

const TimelineBruteIncident = ({ incident, classes, ouvrirInvestigation }) => (
  <div style={{ backgroundColor: '#111827', padding: '12px 16px', fontFamily: 'monospace', fontSize: 12 }}>
    <div style={{ color: '#6b7280', marginBottom: 8, fontSize: 11 }}>
      Timeline brute — {incident.nbEvenements} événement(s) —{' '}
      {formatHeure(new Date(incident.debut))} → {formatHeure(new Date(incident.fin))}{' '}
      ({formatDuree(incident.duree)})
      <Button
        size="small"
        variant="outlined"
        onClick={() => ouvrirInvestigation && ouvrirInvestigation(contexteIncidentOperationnel(incident))}
        style={{ color: '#f9fafb', borderColor: '#4b5563', marginLeft: 12 }}
      >
        Investigation
      </Button>
    </div>
    {incident.evenements.map((ev, idx) => (
      <div
        key={idx}
        style={{ display: 'flex', gap: 12, padding: '2px 0', cursor: 'pointer' }}
        onClick={() => ouvrirInvestigation && ouvrirInvestigation(contexteIncidentOperationnel(incident, ev))}
      >
        <span style={{ color: '#6b7280', minWidth: 56, flexShrink: 0 }}>
          {formatHeureSecondes(ev.ts_event || ev.recu_at)}
        </span>
        <span style={{ color: '#f9fafb', fontWeight: 600, minWidth: 280, flexShrink: 0 }}>
          {ev.evenement_type || '—'}
        </span>
        <span style={{ color: '#9ca3af', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {tronquer(ev.contenu_brut || '', 120)}
        </span>
      </div>
    ))}
    <div style={{ marginTop: 8, color: '#4b5563', fontSize: 11 }}>
      Cliquer une ligne → investigation détaillée
    </div>
  </div>
);

const SectionIncidentsAggreges = ({ donnees, classes, ouvrirInvestigation }) => {
  const [incidentOuvert, setIncidentOuvert] = useState(null);

  const incidents = useMemo(() => {
    const evs = donnees.incidentsCritiques || donnees.incidents || [];
    return agregerEvenementsEnIncidents(evs);
  }, [donnees]);

  const { recents, historiques } = separerRecentsHistoriques(
    incidents,
    (inc) => inc.debut
  );

  if (incidents.length === 0) return null;

  const basculerIncident = (id) => setIncidentOuvert(incidentOuvert === id ? null : id);

  return (
    <Grid item xs={12}>
      <Paper className={classes.paperGuerre}>
        <Typography variant="h6">Incidents opérationnels ({recents.length})</Typography>
        <Typography variant="body2" className={classes.muted}>
          Agrégation déterministe — même caméra, fenêtre 90s, famille logique proche.
          Cliquer un incident pour voir la timeline brute immédiatement.
        </Typography>
        {recents.length > 0 && (
          <div className={classes.tableScroll} style={{ marginTop: 8 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell style={{ width: 24 }} />
                  <TableCell>Caméra</TableCell>
                  <TableCell>Début</TableCell>
                  <TableCell>Durée</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Sévérité</TableCell>
                  <TableCell>Confiance</TableCell>
                  <TableCell>Résumé</TableCell>
                  <TableCell>Évts</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recents.map((inc) => (
                  <React.Fragment key={inc.incidentId}>
                    <TableRow
                      className={classes.rowClickable}
                      onClick={() => basculerIncident(inc.incidentId)}
                    >
                      <TableCell style={{ fontFamily: 'monospace', color: '#6b7280', fontSize: 12 }}>
                        {incidentOuvert === inc.incidentId ? '▼' : '▶'}
                      </TableCell>
                      <TableCell><strong>{inc.cameraId}</strong></TableCell>
                      <TableCell className={classes.signalAge}>{formatHeure(new Date(inc.debut))}</TableCell>
                      <TableCell className={classes.signalAge}>{formatDuree(inc.duree)}</TableCell>
                      <TableCell>
                        <Chip size="small" label={inc.typePrincipal} className={classes.chipBleu} />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={inc.severite}
                          className={chipSeveriteIncident(inc.severite, classes)}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={inc.confiance}
                          className={inc.confiance === 'haute' ? classes.chipVert : classes.chipGris}
                        />
                      </TableCell>
                      <TableCell>{inc.resume}</TableCell>
                      <TableCell>{inc.nbEvenements}</TableCell>
                    </TableRow>
                    {incidentOuvert === inc.incidentId && (
                      <TableRow>
                        <TableCell colSpan={9} style={{ padding: 0 }}>
                          <TimelineBruteIncident
                            incident={inc}
                            classes={classes}
                            ouvrirInvestigation={ouvrirInvestigation}
                          />
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
        <DetailsHistoriques
          classes={classes}
          titre={`Voir incidents historiques > 3 mois (${historiques.length})`}
          visible={historiques.length > 0}
        >
          <div className={classes.tableScroll}>
            <Table size="small">
              <TableBody>
                {historiques.map((inc) => (
                  <TableRow
                    key={inc.incidentId}
                    className={classes.rowClickable}
                    onClick={() => ouvrirInvestigation && ouvrirInvestigation(inc.cameraId)}
                  >
                    <TableCell><strong>{inc.cameraId}</strong></TableCell>
                    <TableCell className={classes.signalAge}>{formatHeure(new Date(inc.debut))}</TableCell>
                    <TableCell className={classes.signalAge}>{formatDuree(inc.duree)}</TableCell>
                    <TableCell><Chip size="small" label={inc.typePrincipal} className={classes.chipBleu} /></TableCell>
                    <TableCell>
                      <Chip size="small" label={inc.severite} className={chipSeveriteIncident(inc.severite, classes)} />
                    </TableCell>
                    <TableCell>{inc.resume}</TableCell>
                    <TableCell>{inc.nbEvenements}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </DetailsHistoriques>
      </Paper>
    </Grid>
  );
};

export default CockpitOperationnel;



// WEBPACK FOOTER //
// src/components/cockpit/CockpitOperationnel.js